/**
 * Cell Value Utilities
 *
 * Shared helpers for accessing cell values in a way that respects
 * the column's accessorFn (important for custom fields).
 */

import type { CellContext } from '@tanstack/react-table';

/**
 * Get raw cell value from context.
 * Uses getValue() first (respects accessorFn), then falls back to direct row access.
 *
 * @param context - The cell context from @tanstack/react-table
 * @param colId - The column ID (used for fallback direct access)
 * @returns The raw value (unknown type - caller should handle conversion)
 */
export const getCellValue = <T extends { id: number | string }>(
  context: CellContext<T, unknown>,
  colId: string
): unknown => {
  // First try getValue() which uses the column's accessorFn
  const accessorValue = context.getValue();
  if (accessorValue !== undefined && accessorValue !== null) {
    return accessorValue;
  }

  // Fallback: direct row access for backward compatibility
  const rowData = context.row.original as Record<string, unknown>;
  return rowData[colId];
};

/**
 * Get cell value as string.
 * Handles object values with 'name' property (common pattern for dropdown values).
 */
export const getCellStringValue = <T extends { id: number | string }>(
  context: CellContext<T, unknown>,
  colId: string
): string => {
  const value = getCellValue(context, colId);

  if (value === undefined || value === null) {
    return '';
  }

  // Handle object values with 'name' property (dropdown pattern)
  if (typeof value === 'object' && 'name' in value) {
    return String((value as { name: string }).name);
  }

  return String(value);
};

/**
 * Get cell value as boolean.
 */
export const getCellBooleanValue = <T extends { id: number | string }>(
  context: CellContext<T, unknown>,
  colId: string
): boolean => {
  const value = getCellValue(context, colId);
  return Boolean(value);
};

/**
 * Get cell value as number (or null if not a valid number).
 */
export const getCellNumberValue = <T extends { id: number | string }>(
  context: CellContext<T, unknown>,
  colId: string
): number | null => {
  const value = getCellValue(context, colId);

  if (value === undefined || value === null) {
    return null;
  }

  const num = Number(value);
  return isNaN(num) ? null : num;
};

/**
 * Get cell value as date string (or null).
 */
export const getCellDateValue = <T extends { id: number | string }>(
  context: CellContext<T, unknown>,
  colId: string
): string | null => {
  const value = getCellValue(context, colId);

  if (value === undefined || value === null) {
    return null;
  }

  return String(value);
};

/**
 * Multi-select value type
 */
export interface MultiSelectValue {
  id: string | number;
  label: string;
  color?: string;
}

/**
 * Parse a value into MultiSelectValue array.
 * Handles various input formats: arrays, comma-separated strings, objects with name/label.
 */
export const parseMultiSelectValue = (value: unknown): MultiSelectValue[] => {
  if (value === undefined || value === null) {
    return [];
  }

  // Already an array
  if (Array.isArray(value)) {
    return value.map((item, index) => {
      if (typeof item === 'object' && item !== null) {
        // Object with id/label or id/name
        const obj = item as Record<string, unknown>;
        const id = 'id' in obj ? (obj.id as string | number) : index;
        const label = 'label' in obj ? obj.label : 'name' in obj ? obj.name : String(item);
        const color = 'color' in obj ? (obj.color as string | undefined) : undefined;
        return { id, label: String(label), ...(color && { color }) };
      }
      // Primitive value
      return { id: String(item), label: String(item) };
    });
  }

  // Comma-separated string
  if (typeof value === 'string') {
    if (!value.trim()) return [];
    return value.split(',').map(s => {
      const trimmed = s.trim();
      return { id: trimmed, label: trimmed };
    });
  }

  // Single object with name/label
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;
    const label = obj.label ?? obj.name ?? String(value);
    const id = obj.id ?? label;
    return [{ id: id as string | number, label: String(label) }];
  }

  // Single primitive
  return [{ id: String(value), label: String(value) }];
};

/**
 * Get cell value as MultiSelectValue array.
 */
export const getCellMultiSelectValue = <T extends { id: number | string }>(
  context: CellContext<T, unknown>,
  colId: string
): MultiSelectValue[] => {
  const value = getCellValue(context, colId);
  return parseMultiSelectValue(value);
};
